import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, XCircle, ArrowRight } from "lucide-react";
import { groundOpsApi } from "@/api";

const SEV_STYLE = {
  critical: "bg-red-600/20 border-red-600/40 text-red-300",
  high:     "bg-red-500/10 border-red-500/30 text-red-400",
  warning:  "bg-amber-500/10 border-amber-500/30 text-amber-400",
  medium:   "bg-amber-500/10 border-amber-500/30 text-amber-400",
  incident: "bg-purple-500/10 border-purple-500/30 text-purple-400",
  low:      "bg-slate-500/10 border-slate-500/30 text-slate-400",
};

function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" });
}

export default function ExceptionView() {
  const [exceptions, setExceptions] = useState([]);
  const [alerts, setAlerts]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([groundOpsApi.getExceptions(), groundOpsApi.getAlerts()])
      .then(([ex, al]) => { setExceptions(ex); setAlerts(al); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 text-center text-slate-500 animate-pulse">Loading exceptions...</div>;

  return (
    <div className="p-5 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <AlertTriangle size={18} className="text-red-400" />
        <h1 className="text-base font-black uppercase tracking-widest text-white">Exceptions & Alerts</h1>
        {(exceptions.length + alerts.length) > 0 && (
          <span className="bg-red-500 text-white text-[9px] font-black rounded-full px-2 py-0.5">{exceptions.length + alerts.length} active</span>
        )}
      </div>

      {/* ── Active Alerts ──────────────────────────────────────────────── */}
      {alerts.length > 0 && (
        <div className="mb-6">
          <h2 className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Live Alerts</h2>
          <div className="space-y-2">
            {alerts.map(a => (
              <div key={a.id} className={`flex items-start gap-3 p-3 rounded-lg border ${SEV_STYLE[a.severity] || SEV_STYLE.warning}`} data-testid={`alert-${a.id}`}>
                <XCircle size={14} className="flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold text-sm">{a.flight_number}</span>
                    <span className="text-[9px] bg-white/10 rounded px-1.5 py-0.5 uppercase">{a.station}</span>
                    <span className="text-[10px] font-semibold">{a.task_name}</span>
                  </div>
                  {a.comments && <div className="text-[10px] opacity-80 mt-0.5 truncate">{a.comments}</div>}
                </div>
                <span className="text-[9px] font-mono opacity-60 flex-shrink-0">{fmtTime(a.time)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── At-Risk / Delayed Flights ──────────────────────────────────── */}
      {exceptions.length === 0 && alerts.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-60 text-slate-600">
          <AlertTriangle size={32} className="mb-3 opacity-20" />
          <p className="text-sm">No exceptions — all flights running normally</p>
        </div>
      ) : (
        <div className="space-y-4">
          <h2 className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">At-Risk Flights</h2>
          {exceptions.map(f => (
            <div key={f.id} className="bg-[#0e1e31] border border-red-500/25 rounded-xl p-4" data-testid={`exception-${f.flight_number.replace(" ","")}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-black font-mono text-white">{f.flight_number}</span>
                    <span className="text-slate-400 font-mono">{f.origin} → {f.destination}</span>
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded border ${f.risk_status === "delayed" ? "bg-red-500/15 text-red-400 border-red-500/30" : "bg-amber-500/15 text-amber-400 border-amber-500/30"}`}>
                      {f.delay_risk_label}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{f.station} · Stand {f.stand} · {f.aircraft_reg}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-2xl font-black font-mono ${f.delay_risk_score >= 65 ? "text-red-400" : "text-amber-400"}`}>{f.delay_risk_score}</span>
                  <button
                    onClick={() => navigate(`/turnaround/flight/${f.id}`)}
                    className="flex items-center gap-1 text-[10px] text-orange-400 hover:text-orange-300 border border-orange-500/30 rounded px-2 py-1 transition"
                  >
                    Details <ArrowRight size={9} />
                  </button>
                </div>
              </div>
              {/* Problematic tasks */}
              {(f.tasks || []).length > 0 && (
                <div className="space-y-1 mt-2">
                  {f.tasks.map(t => (
                    <div key={t.id} className={`flex items-center gap-2 text-[10px] rounded px-2 py-1.5 ${t.status === "blocked" ? "bg-purple-500/10 text-purple-300" : "bg-red-500/10 text-red-300"}`}>
                      <AlertTriangle size={9} />
                      <span className="font-semibold">{t.name}</span>
                      <span className="uppercase text-[8px] opacity-70">{t.status}</span>
                      {t.comments && <span className="text-[9px] opacity-60 truncate ml-2">{t.comments}</span>}
                    </div>
                  ))}
                </div>
              )}
              {f.current_bottleneck && (
                <div className="text-[10px] text-amber-400/70 mt-2">
                  <AlertTriangle size={8} className="inline mr-1" /> {f.current_bottleneck}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
